/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import {
  CSSProperties,
  PropsWithChildren,
  ReactNode,
  createContext,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react'

import { nanoid } from 'nanoid'
import { Interpolation } from 'styled-components'

import { CouldBe } from 'src/types/main'

type ModalV2ContextType = {
  hasActiveModals: boolean
  modalsStack: Map<string, ModalV2Config>
  closeModal: (key: string) => void
  closeTopmostModal: () => void
  openModal: (config: Partial<ModalV2Config>, key?: string) => void
}

export type ModalV2Config = {
  content: ReactNode
  dialogStyle: CouldBe<Interpolation<CSSProperties>>
  dialogTitle: string
  fullscreen: boolean
  showCloseButton: boolean
  showDialogFooter: boolean
  showDialogHeader: boolean
}

const INITIAL_MODAL_V2_CONFIG: ModalV2Config = {
  content: undefined,
  dialogStyle: undefined,
  dialogTitle: '',
  fullscreen: false,
  showCloseButton: true,
  showDialogFooter: true,
  showDialogHeader: true
}

const INITIAL_MODAL_V2_CONTEXT: ModalV2ContextType = {
  modalsStack: new Map(),
  hasActiveModals: false,
  closeModal: () => {
    throw new Error('closeModal function must be overidden before use')
  },
  closeTopmostModal: () => {
    throw new Error('closeTopmostModal function must be overidden before use')
  },
  openModal: () => {
    throw new Error('openModal function must be overidden before use')
  }
}

export const ModalV2Context = createContext(INITIAL_MODAL_V2_CONTEXT)

export const ModalV2ContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const stackRef = useRef<Map<string, ModalV2Config>>(new Map())

  const [modalsStack, setModalsStack] = useState<Map<string, ModalV2Config>>(new Map())
  const [stackItemKeys, setStackItemKeys] = useState<string[]>([])

  const hasActiveModals = useMemo(() => modalsStack.size > 0, [modalsStack.size])

  const closeModal = useCallback((key: string) => {
    const modalsStack = stackRef.current
    if (!modalsStack.has(key)) {
      console.error(`Modal with ${key} key is not exist. No modal will be closed.`)
      return
    }

    modalsStack.delete(key)
    setModalsStack(modalsStack)
    setStackItemKeys((prev) => prev.filter((prevKey) => prevKey !== key))
  }, [])

  const closeTopmostModal = useCallback(() => {
    const latestModalKey = stackItemKeys.at(-1)
    if (!latestModalKey) {
      console.warn(`No modal is currently showing.`)
      return
    }

    closeModal(latestModalKey)
  }, [closeModal, stackItemKeys])

  const openModal = useCallback<ModalV2ContextType['openModal']>((config, key) => {
    const modalStack = stackRef.current
    const _key = key || nanoid()
    if (modalStack.has(_key)) {
      console.error(`Another modal with ${_key} key already exist. Please re-configure your modal.`)
      return
    }

    modalStack.set(_key, { ...INITIAL_MODAL_V2_CONFIG, ...config })
    setModalsStack(modalStack)
    setStackItemKeys((prev) => [...prev, _key])
  }, [])

  return (
    <ModalV2Context.Provider
      value={{
        hasActiveModals,
        modalsStack,
        closeModal,
        closeTopmostModal,
        openModal
      }}
      children={children}
    />
  )
}
